import {
  ProductDescription,
  ProductDetails,
} from "@src/core/engine/products/types";
import { useConvertedValueFormat } from "@src/shared/hooks/useConvertedValueFormat";
import { useUnitConversion } from "@src/shared/hooks/useUnitConversion";
import { group, listify } from "radashi";
import { useCallback } from "react";

export type GetGroupedProductDescriptions = (
  product: ProductDetails,
) => FormattedProductDescription[];

type FormatDescriptionsOptions = {
  excludeSymbol?: boolean;
  targetDecimals?: number | null;
};

type FormattedProductDescription = {
  description: string | null | undefined;
  id: string;
  translationKey: string;
};

export function useGetProductDescriptions() {
  const { convertToUserUnit, getUnitSymbol } = useUnitConversion();
  const formatValue = useConvertedValueFormat();

  const formatDescriptions = useCallback(
    (
      items: ProductDescription[] | undefined,
      { excludeSymbol = false, targetDecimals }: FormatDescriptionsOptions = {},
    ) => {
      const physicalType = items?.[0]?.physicalType;
      if (!physicalType) {
        return items?.map((item) => item.value).join("/");
      }

      if (physicalType === "PHYS_LENGTH") {
        const formatted = items
          ?.map((item) =>
            formatValue(item.value, item.physicalType, convertToUserUnit, {
              excludeSymbol: true,
              targetDecimals,
            }),
          )
          .join(" x ");
        return formatted ? `${formatted} ${getUnitSymbol(physicalType)}` : "";
      }

      return items
        ?.map((item) =>
          formatValue(item.value, item.physicalType, convertToUserUnit, {
            excludeSymbol,
            targetDecimals,
          }),
        )
        .join("/");
    },
    [formatValue, getUnitSymbol, convertToUserUnit],
  );

  return {
    formatDescriptions,
    getProductDescriptions: useCallback(
      (product: ProductDetails) =>
        listify(
          group(product.description, (desc) => desc.translationKey),
          (translationKey, items) => {
            const filtered = items?.filter((item) => item.value);
            return {
              description: filtered?.length
                ? formatDescriptions(filtered)
                : null,
              id: items?.[0]?.id ?? "",
              translationKey,
            };
          },
        ),
      [formatDescriptions],
    ),
  };
}
